import React, { useState, useEffect } from 'react';
import { TYPES, TYPE_COLORS, TYPE_CARD } from '../data/types';
import PlayingCard from '../components/PlayingCard';
import './Results.css';

const COMPAT = {
  INTJ:['ENFP','ENTP'], INTP:['ENTJ','ENFJ'], ENTJ:['INTP','INFP'], ENTP:['INTJ','INFJ'],
  INFJ:['ENTP','ENFP'], INFP:['ENTJ','ENFJ'], ENFJ:['INTP','ISFP'], ENFP:['INTJ','INFJ'],
  ISTJ:['ESFP','ESTP'], ISFJ:['ESFP','ESTP'], ESTJ:['ISFP','ISTP'], ESFJ:['ISFP','ISTP'],
  ISTP:['ESFJ','ESTJ'], ISFP:['ENFJ','ESFJ'], ESTP:['ISFJ','ISTJ'], ESFP:['ISFJ','ISTJ'],
};

export default function Results({ result, email }) {
  // result is now 'INTJ-T' or 'INFJ-A' format
  const mbtiCode = result ? result.split('-')[0] : 'INFP';
  const atCode   = result ? (result.split('-')[1] || 'A') : 'A';
  const [tab, setTab] = useState('overview');
  const data   = TYPES[mbtiCode] || TYPES['INFP'];
  const colors = TYPE_COLORS[mbtiCode] || TYPE_COLORS['INFP'];
  const card   = TYPE_CARD[mbtiCode];
  const compat = COMPAT[mbtiCode] || ['ENFP','INTJ'];

  useEffect(() => { window.scrollTo(0,0); }, []);

  const dims = [
    { left:'Extrovertiert (E)', right:'Introvertiert (I)', val: mbtiCode[0]==='E' ? 72 : 28 },
    { left:'Wahrnehmend (S)',   right:'Intuitiv (N)',      val: mbtiCode[1]==='S' ? 65 : 35 },
    { left:'Denkend (T)',       right:'Fühlend (F)',       val: mbtiCode[2]==='T' ? 70 : 30 },
    { left:'Urteilend (J)',     right:'Wahrnehmend (P)',   val: mbtiCode[3]==='J' ? 68 : 32 },
    { left:'Assertiv (A)',       right:'Turbulent (T)',      val: atCode==='A' ? 71 : 29 },
  ];

  const TABS = ['overview','strengths','relationships','career'];
  const TAB_LABELS = { overview:'Übersicht', strengths:'Stärken', relationships:'Beziehungen', career:'Karriere' };

  return (
    <div className="results-page">
      <nav className="results-nav">
        <span className="logo-text" style={{fontFamily:'Nunito,sans-serif',fontSize:20,fontWeight:900,letterSpacing:'-0.03em'}}>
          <span style={{color:'var(--teal)'}}>ICH</span><span style={{color:'var(--text)'}}>TYPE</span><span style={{fontSize:11,fontWeight:700,color:'var(--text3)',marginLeft:1}}>.DE</span>
        </span>
        <button className="share-btn" onClick={() => {
          const text = `Ich bin ${result} – ${data.name}! ${card.rankName} ${card.suitName}. Finde deinen Typ: ichtype.de`;
          navigator.share ? navigator.share({text, url:'https://ichtype.de'}) : navigator.clipboard.writeText(text).then(()=>alert('Kopiert!'));
        }}>📤 Teilen</button>
      </nav>

      {/* HERO — PLAYING CARD REVEAL */}
      <div className="result-hero" style={{background:`linear-gradient(160deg, ${colors.bg} 0%, #ffffff 100%)`}}>
        <div className="result-hero-inner">
          <div className="result-card-wrap">
            <PlayingCard typeCode={mbtiCode} size="large" />
          </div>
          <div className="result-hero-text">
            <div className="rarity-badge" style={{background:colors.bg, borderColor:colors.border, color:colors.accent}}>
              ✦ {data.rarity} · Nur {data.percent} der Bevölkerung
            </div>
            <h1 className="result-code-big" style={{color:colors.accent}}>{result}</h1>
            <h2 className="result-name-big">{data.name}</h2>
            <p className="result-tagline">{data.tagline}</p>
            <div className="at-badge-wrap">
              <div className={`at-badge ${atCode === "A" ? "at-a" : "at-t"}`}>
                <span className="at-letter">{mbtiCode}-{atCode}</span>
                <span className="at-label">{atCode === "A" ? "Assertiv — Selbstsicher & Stressresistent" : "Turbulent — Ehrgeizig & Selbstkritisch"}</span>
              </div>
              <div className="at-sub">{atCode === "A" ? "Du bist im Allgemeinen ruhig, gelassen und zufrieden mit dir selbst." : "Du hast hohe Ansprüche an dich selbst und strebst ständig nach Verbesserung."}</div>
            </div>
            <div className="card-identity" style={{background:colors.bg, borderColor:colors.border}}>
              <span style={{color:colors.accent, fontSize:28}}>{card.suit}</span>
              <div>
                <div style={{fontWeight:800, color:colors.accent}}>{card.rankName} {card.suitName}</div>
                <div style={{fontSize:12, color:'var(--text3)'}}>Deine Spielkarte</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* EMAIL SENT NOTICE */}
      {email && (
        <div className="email-notice">
          ✉️ Vollständiger Bericht gesendet an <strong>{email}</strong>
        </div>
      )}

      {/* TABS */}
      <div className="tabs-wrap">
        <div className="tabs">
          {TABS.map(t => (
            <button key={t} className={`tab ${tab===t?'active':''}`} onClick={()=>setTab(t)}>
              {TAB_LABELS[t]}
            </button>
          ))}
        </div>
      </div>

      <div className="result-body">

        {tab === 'overview' && (
          <div className="tab-panel">
            <div className="r-section">
              <h2 className="r-title">Dein Profil</h2>
              <p className="r-text">{data.description}</p>
            </div>
            <div className="r-section">
              <h2 className="r-title">Deine vier Dimensionen</h2>
              <div className="dims">
                {dims.map((d,i) => (
                  <div key={i} className="dim-row">
                    <div className="dim-left" style={{color: d.val>50 ? colors.accent : 'var(--text4)'}}>{d.left}</div>
                    <div className="dim-track"><div className="dim-fill" style={{width:`${d.val}%`, background:`linear-gradient(90deg, ${colors.accent}, ${colors.border})`}} /></div>
                    <div className="dim-right" style={{color: d.val<50 ? colors.accent : 'var(--text4)'}}>{d.right}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="r-section">
              <h2 className="r-title">Bekannte {result}s</h2>
              <div className="famous-chips">
                {data.famous.map(f => <div key={f} className="famous-chip">⭐ {f}</div>)}
              </div>
            </div>
          </div>
        )}

        {tab === 'strengths' && (
          <div className="tab-panel">
            <div className="r-section">
              <h2 className="r-title">✅ Deine Stärken</h2>
              <div className="traits-grid">
                {data.strengths.map(s => <div key={s} className="trait trait-s">✓ {s}</div>)}
              </div>
            </div>
            <div className="r-section">
              <h2 className="r-title">⚠️ Herausforderungen</h2>
              <div className="traits-grid">
                {data.challenges.map(c => <div key={c} className="trait trait-c">△ {c}</div>)}
              </div>
            </div>
          </div>
        )}

        {tab === 'relationships' && (
          <div className="tab-panel">
            <div className="r-section">
              <h2 className="r-title">💞 Kompatible Typen</h2>
              <div className="compat-cards-grid">
                {compat.map(t => (
                  <div key={t} style={{display:'flex',flexDirection:'column',alignItems:'center',gap:12}}>
                    <PlayingCard typeCode={t} size="medium" />
                    <div style={{textAlign:'center'}}>
                      <div style={{fontWeight:800, color:TYPE_COLORS[t]?.accent}}>{t}</div>
                      <div style={{fontSize:13, color:'var(--text3)'}}>{TYPES[t]?.name}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="r-section">
              <h2 className="r-title">Du in Beziehungen</h2>
              <p className="r-text">Als {result} bringst du {data.strengths[0].toLowerCase()} und {data.strengths[1].toLowerCase()} in deine Beziehungen ein. Deine größte Herausforderung ist {data.challenges[0].toLowerCase()}.</p>
            </div>
          </div>
        )}

        {tab === 'career' && (
          <div className="tab-panel">
            <div className="r-section">
              <h2 className="r-title">💼 Deine idealen Berufsfelder</h2>
              <div className="career-grid">
                {getCareers(result).map(c => (
                  <div key={c.title} className="career-card">
                    <div className="career-icon">{c.icon}</div>
                    <div className="career-title">{c.title}</div>
                    <div className="career-desc">{c.desc}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="share-box" style={{background:`linear-gradient(135deg, ${colors.accent}, ${colors.border})`}}>
          <h3>Du bist der {card.rankName} {card.suitName}!</h3>
          <p>Teile deine Karte und finde heraus was deine Freunde sind.</p>
          <button className="btn-share" onClick={() => {
            const text = `Ich bin ${result} – ${data.name} (${card.rankName} ${card.suitName})! Finde deinen Typ: ichtype.de`;
            navigator.share ? navigator.share({text}) : navigator.clipboard.writeText(text).then(()=>alert('Kopiert!'));
          }}>🃏 Karte teilen</button>
        </div>

      </div>
    </div>
  );
}

function getCareers(type) {
  const m = {
    INTJ:[{icon:'💻',title:'Technologie & IT',desc:'Software-Architektur, KI, Systemdesign'},{icon:'📊',title:'Strategie',desc:'Unternehmensberatung, Strategieentwicklung'},{icon:'🔬',title:'Wissenschaft',desc:'Grundlagenforschung, Analytik'}],
    INTP:[{icon:'🔬',title:'Forschung',desc:'Wissenschaft, Mathematik, Philosophie'},{icon:'💻',title:'Programmierung',desc:'Algorithmen, KI, Theoretische Informatik'},{icon:'📚',title:'Akademia',desc:'Lehre, Theorieentwicklung'}],
    ENTJ:[{icon:'🏢',title:'CEO / Führung',desc:'Unternehmensführung, Executive Management'},{icon:'⚖️',title:'Jura',desc:'Anwalt, Richter, Unternehmensrecht'},{icon:'📈',title:'Investment',desc:'Venture Capital, Private Equity'}],
    ENTP:[{icon:'🚀',title:'Startups',desc:'Gründung, Innovation, Disruption'},{icon:'🎤',title:'Consulting',desc:'Strategieberatung, Keynote'},{icon:'📱',title:'Produktentwicklung',desc:'Product Management, UX'}],
    INFJ:[{icon:'🧠',title:'Psychologie',desc:'Therapie, Coaching, Beratung'},{icon:'✍️',title:'Schreiben',desc:'Autor, Journalist, Content'},{icon:'🌱',title:'NGO',desc:'Non-profit, Sozialarbeit'}],
    INFP:[{icon:'🎨',title:'Kreativwirtschaft',desc:'Design, Kunst, Fotografie'},{icon:'✍️',title:'Literatur',desc:'Autor, Poet, Drehbuchschreiber'},{icon:'🧠',title:'Psychologie',desc:'Therapie, Beratung'}],
    ENFJ:[{icon:'🧑‍🏫',title:'Bildung',desc:'Lehrer, Professor, Trainer'},{icon:'🌍',title:'Politik',desc:'Politiker, Aktivismus, NGO'},{icon:'🎤',title:'Coaching',desc:'Life Coach, Speaker, Mentor'}],
    ENFP:[{icon:'🎨',title:'Kreativ',desc:'Marketing, Design, Content Creation'},{icon:'🧑‍🏫',title:'Coaching',desc:'Training, Mentoring, Unterricht'},{icon:'🌍',title:'Soziales',desc:'Non-profit, Community Management'}],
    ISTJ:[{icon:'⚖️',title:'Recht & Ordnung',desc:'Jura, Verwaltung, Compliance'},{icon:'💰',title:'Finanzen',desc:'Buchhaltung, Controlling, Banking'},{icon:'🏗️',title:'Ingenieurwesen',desc:'Bauplanung, Qualitätssicherung'}],
    ISFJ:[{icon:'🏥',title:'Gesundheit',desc:'Pflege, Medizin, Sozialarbeit'},{icon:'🧑‍🏫',title:'Bildung',desc:'Lehrerin, Erzieherin, Betreuung'},{icon:'🏛️',title:'Verwaltung',desc:'Öffentlicher Dienst, HR, Support'}],
    ESTJ:[{icon:'🏢',title:'Management',desc:'Führung, Organisation, Planung'},{icon:'⚖️',title:'Jura',desc:'Richter, Staatsanwalt, Compliance'},{icon:'🔐',title:'Sicherheit',desc:'Polizei, Militär, Ordnung'}],
    ESFJ:[{icon:'🏥',title:'Gesundheit',desc:'Arzt, Krankenpflege, Therapie'},{icon:'🧑‍🏫',title:'Bildung',desc:'Lehrer, Erzieher, Sozialpädagoge'},{icon:'🤝',title:'HR & Events',desc:'Personalwesen, Veranstaltungen'}],
    ISTP:[{icon:'🔧',title:'Handwerk & Technik',desc:'Maschinenbau, Elektronik, IT'},{icon:'🚑',title:'Notfalldienste',desc:'Rettungsdienst, Feuerwehr, Chirurgie'},{icon:'✈️',title:'Transport',desc:'Pilot, Mechaniker, Ingenieur'}],
    ISFP:[{icon:'🎨',title:'Kunst & Design',desc:'Bildende Kunst, Fotografie, Mode'},{icon:'🌿',title:'Natur',desc:'Tierpflege, Ökologie, Landschaft'},{icon:'🍳',title:'Kulinarik',desc:'Koch, Bäcker, Ernährungsberater'}],
    ESTP:[{icon:'💼',title:'Vertrieb',desc:'Sales, Business Development'},{icon:'📈',title:'Trading',desc:'Börse, Immobilien, Investment'},{icon:'🚔',title:'Notfall & Action',desc:'Polizei, Sport, Rettung'}],
    ESFP:[{icon:'🎭',title:'Entertainment',desc:'Schauspiel, Musik, Comedy'},{icon:'🎤',title:'Events',desc:'Eventmanager, PR, Moderation'},{icon:'🏋️',title:'Sport & Fitness',desc:'Trainer, Sportler, Wellness'}],
  };
  return m[type] || [{icon:'💼',title:'Management',desc:'Führung, Organisation'},{icon:'🤝',title:'Beratung',desc:'Consulting, Coaching'},{icon:'🎯',title:'Projektmanagement',desc:'Koordination, Planung'}];
}
